import React, { useState } from 'react';
import './VideoLibrary.css';

const categoryIcons = {
  "Chess Fundamentals": "📋",
  "Piece Movement & Rules": "♟",
  "Tactics & Checkmates": "⚔️",
  "Advanced Techniques": "🎯",
};

const VideoLibrary = () => {
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [openSections, setOpenSections] = useState({});
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const videoData = {
    "Chess Fundamentals": [
      {
        id: 1,
        title: "Files and Ranks",
        duration: "3:45",
        difficulty: "beginner",
        thumbnail: "📐",
        description: "Learn the basic coordinate system of the chess board - files and ranks.",
        videoFile: "/videos/files_and_ranks.mp4"
      }
    ],
    "Piece Movement & Rules": [
      {
        id: 2,
        title: "How Pawns Move",
        duration: "6:20",
        difficulty: "beginner",
        thumbnail: "♟️",
        description: "Master the movement of pawns, the most numerous pieces on the board.",
        videoFile: "/videos/How_Pawns_Move.mp4"
      },
      {
        id: 3,
        title: "How Knights Move",
        duration: "4:35",
        difficulty: "beginner",
        thumbnail: "♞",
        description: "Learn the unique L-shaped movement pattern of the knight.",
        videoFile: "/videos/How_Knights_Move.mp4"
      },
      {
        id: 4,
        title: "How Bishops Move",
        duration: "5:45",
        difficulty: "beginner",
        thumbnail: "♝",
        description: "Master diagonal movement with the bishop across the board.",
        videoFile: "/videos/How_Bishop_Move.mp4"
      },
      {
        id: 5,
        title: "How Rooks Move and Castling",
        duration: "6:20",
        difficulty: "beginner",
        thumbnail: "♜",
        description: "Learn rook movement and the special castling maneuver.",
        videoFile: "/videos/Rooks_Moves_and_Castling.mp4"
      },
      {
        id: 6,
        title: "How Queens Move",
        duration: "3:15",
        difficulty: "beginner",
        thumbnail: "♕",
        description: "Master the most powerful piece - the queen's movement.",
        videoFile: "/videos/How_Queens_Move.mp4"
      },
      {
        id: 7,
        title: "How Kings Move",
        duration: "3:45",
        difficulty: "beginner",
        thumbnail: "♔",
        description: "Learn the king's movement and its importance in the game.",
        videoFile: "/videos/How_Kings_Move.mp4"
      },
      {
        id: 8,
        title: "En Passant",
        duration: "7:25",
        difficulty: "intermediate",
        thumbnail: "⚡",
        description: "Master the special pawn capture rule - en passant.",
        videoFile: "/videos/En_passant.mp4"
      }
    ],
    "Tactics & Checkmates": [
      {
        id: 9,
        title: "Check",
        duration: "3:50",
        difficulty: "beginner",
        thumbnail: "⚠️",
        description: "Learn what a check is and how to respond to it.",
        videoFile: "/videos/Check.mp4"
      },
      {
        id: 10,
        title: "Checkmate",
        duration: "1:45",
        difficulty: "beginner",
        thumbnail: "🏆",
        description: "Understand the ultimate goal - achieving checkmate.",
        videoFile: "/videos/Checkmate.mp4"
      },
      {
        id: 11,
        title: "Checkmate with Queen",
        duration: "7:45",
        difficulty: "intermediate",
        thumbnail: "♕",
        description: "Learn effective queen checkmate patterns and techniques.",
        videoFile: "/videos/Checkmate_with_Queen.mp4"
      },
      {
        id: 12,
        title: "Checkmate with Rook",
        duration: "7:20",
        difficulty: "intermediate",
        thumbnail: "♜",
        description: "Master the technique of checkmating with a rook.",
        videoFile: "/videos/Checkmate_with_Rook.mp4"
      }
    ],
    "Advanced Techniques": [
      {
        id: 13,
        title: "Ladder Checkmate",
        duration: "4:50",
        difficulty: "intermediate",
        thumbnail: "🪜",
        description: "Learn the ladder checkmate technique with two rooks.",
        videoFile: "/videos/Ladder_checkmate.mp4"
      },
      {
        id: 14,
        title: "Material and Examples",
        duration: "10:45",
        difficulty: "intermediate",
        thumbnail: "⚖️",
        description: "Understand material value and see practical examples.",
        videoFile: "/videos/Material_and_Example.mp4"
      }
    ]
  };

  const toggleSection = (category) => {
    setOpenSections(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const difficultyOrder = ['beginner', 'intermediate', 'advanced'];

  const getSectionDifficulties = (videos) => {
    const unique = new Set(videos.map(v => v.difficulty));
    return difficultyOrder.filter(d => unique.has(d));
  };

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setSelectedVideo(null);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'difficulty-beginner';
      case 'intermediate': return 'difficulty-intermediate';
      case 'advanced': return 'difficulty-advanced';
      default: return 'difficulty-beginner';
    }
  };

  const filterVideos = (videos) => {
    if (selectedDifficulty === 'all') return videos;
    return videos.filter(video => video.difficulty === selectedDifficulty);
  };

  return (
    <div className="video-library">
      <section className="hero-section">
        <div className="container">
          <h1>Chess Video Library</h1>
          <p>Master chess with our comprehensive collection of instructional videos</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="filters">
            <h3>Filter by Difficulty:</h3>
            <div className="difficulty-filters">
              {['all', 'beginner', 'intermediate', 'advanced'].map(level => (
                <button
                  key={level}
                  className={`filter-btn ${selectedDifficulty === level ? 'active' : ''}`}
                  onClick={() => setSelectedDifficulty(level)}
                >
                  {level === 'all' ? 'All Levels' : level.charAt(0).toUpperCase() + level.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="accordion-list">
            {Object.entries(videoData).map(([category, videos]) => {
              const filteredVideos = filterVideos(videos);
              if (filteredVideos.length === 0) return null;
              const isOpen = !!openSections[category];
              const sectionDifficulties = getSectionDifficulties(videos);

              return (
                <div key={category} className={`accordion-item ${isOpen ? 'open' : ''}`}>
                  <button className="accordion-header" onClick={() => toggleSection(category)}>
                    <div className="accordion-header-left">
                      <span className="accordion-icon">{categoryIcons[category]}</span>
                      <div className="accordion-title-group">
                        <span className="accordion-title">{category}</span>
                        <div className="accordion-difficulty-badges">
                          {sectionDifficulties.map(d => (
                            <span key={d} className={`section-difficulty-badge section-difficulty-${d}`}>{d}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="accordion-header-right">
                      <span className="accordion-count">
                        {filteredVideos.length} {filteredVideos.length === 1 ? 'video' : 'videos'}
                      </span>
                      <svg
                        className={`accordion-chevron ${isOpen ? 'open' : ''}`}
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="accordion-content">
                      <div className="video-grid">
                        {filteredVideos.map((video) => (
                          <div
                            key={video.id}
                            className="video-card"
                            onClick={() => handleVideoClick(video)}
                          >
                            <div className="video-thumbnail">
                              {video.thumbnail}
                            </div>
                            <div className="video-info">
                              <h3 className="video-title">{video.title}</h3>
                              <div className="video-meta">
                                <div className="video-duration">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                  </svg>
                                  {video.duration}
                                </div>
                                <span className={`difficulty-badge ${getDifficultyColor(video.difficulty)}`}>
                                  {video.difficulty}
                                </span>
                              </div>
                              <p className="video-description">{video.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {isVideoModalOpen && selectedVideo && (
        <div className="video-modal-overlay" onClick={closeVideoModal}>
          <div className="video-modal" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <h2>{selectedVideo.title}</h2>
              <button className="close-btn" onClick={closeVideoModal}>×</button>
            </div>
            <div className="video-player-container">
              <video controls className="video-player" preload="metadata">
                <source src={selectedVideo.videoFile} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="video-modal-info">
              <div className="video-meta">
                <span className="duration">Duration: {selectedVideo.duration}</span>
                <span className={`difficulty-badge ${getDifficultyColor(selectedVideo.difficulty)}`}>
                  {selectedVideo.difficulty}
                </span>
              </div>
              <p className="description">{selectedVideo.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoLibrary;
